import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import validator from "validator";

// Imported actions
import { postContact } from "../../../../actions/alert.js";

// Imported assets
// import CloseIcon from "../../../assets/icons/close.svg";

// Imported components
import Info from "../../../../components/form/Info.js";
import PrimaryButton from "../../../../components/ui/PrimaryButton.js";

// Imported utils
import { isLoggedIn } from "../../../../utils/user.js";

// Styled components
const ButtonGroup = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: space-between;
  position: relative;
  margin-top: 16px;
`;

const CancelButton = styled(PrimaryButton)`
  background: white;
  border: 1px solid #d9d9d9;
  color: black;

  &:hover {
    background: lab(from white calc(l - 5) a b / 100);
  }
`;

// const CloseButton = styled.img`
//   background-color: transparent;
//   border: none;
//   font-size: 16px;
//   cursor: pointer;
//   color: #333;
//   position: absolute;
//   margin: 10px;
//   right: 0px;
//   top: 0px;
//   width: 50px;
//   z-index: 120;
// `;

const Form = styled.form`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  position: relative;
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
  padding: 2px;
  position: relative;
  width: 100%;
`;

const FormHeader = styled.h1`
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-weight: 300;
`;

const FormRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: space-between;
  margin-top: 20px;
  position: relative;
  width: 100%;

  &:first-child {
    margin-top: 40px;
  }
`;

interface ModalProps {
  $isOpen?: boolean;
}

const Modal = styled.div<ModalProps>`
  background-color: #f2f2f2;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  left: 50%;
  max-width: 80%;
  overflow-y: auto;
  padding: 20px 40px;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 110;
`;

interface OverlayProps {
  $isOpen?: boolean;
}

const Overlay = styled.div<OverlayProps>`
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 109;
`;

const SaveButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  color: white;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const NewContactModal = ({
  isNewContactModalOpen,
  toggleContactModal,
}) => {
  const appLocation = useLocation();
  const navigate = useNavigate();

  const [contactName, setContactName] = useState<string>("");
  const [contactNameErr, setContactNameErr] = useState<string | null>(null);

  const [email, setEmail] = useState<string>("");
  const [emailErr, setEmailErr] = useState<string | null>(null);

  const [phone, setPhone] = useState<string>("");
  const [phoneErr, setPhoneErr] = useState<string | null>(null);

  const handleClear = () => {
    setContactName("");
    setContactNameErr(null);

    setEmail("");
    setEmailErr(null);

    setPhone("");
    setPhoneErr(null);
  };

  // Toggle modal open state based on current state
  const toggleModal = () => {
    handleClear();
    toggleContactModal(!isNewContactModalOpen);
  };

  const validateInput = () => {
    let contactNameIsValid = false,
      emailIsValid = false,
      phoneIsValid = false;

    // Contact Name validation
    if (contactName) {
      if (contactName.length < 1) setContactNameErr("Contact name is too short");
      else if (contactName.length > 64) setContactNameErr("Contact name is too long");
      else {
        contactNameIsValid = true;
        setContactNameErr(null);
      }
    } else setContactNameErr("Contact name is required");

    // Email validation
    if (email) {
      if (validator.isEmail("" + email)) {
        emailIsValid = true;
        setEmailErr(null);
      } else setEmailErr("Email is invalid");
    } else setEmailErr("Email is required");

    // SMS / Phone number validation
    if (phone) {
      if (validator.isMobilePhone("" + phone, "any")) {
        phoneIsValid = true;
        setPhoneErr(null);
      } else setPhoneErr("Mobile phone is invalid");
    } else setPhoneErr("Mobile phone is required");

    return (
      contactNameIsValid &&
      emailIsValid &&
      phoneIsValid
    );
  };

  const handleSaveContact = async (e) => {
    e?.preventDefault();

    if (validateInput()) {
      if (!isLoggedIn()) {
        navigate("/login", {
          replace: true,
          state: { path: appLocation.pathname },
        });
      } else {
        await postContact(contactName, email, phone)
          .then((resp) => {
            toggleModal();
            // setToastMessage("");
            // setToastSeverity("success");
          })
          .catch((err) => {
            // setToastMessage("");
            // setToastSeverity("error");
          });
      }
    }
  };

  return (
    <div>
      <Overlay $isOpen={isNewContactModalOpen} onClick={toggleModal} />

      <Modal $isOpen={isNewContactModalOpen} id="modal">
        {/* <CloseButton onClick={toggleModal} src={CloseIcon} /> */}

        <Form onSubmit={(e) => handleSaveContact(e)}>
          <FormHeader>Create New Contact</FormHeader>
          <FormRow>
            {/* <FormField> */}
              <FormControl sx={{ bgcolor: "white", minWidth: 300, width: "100%" }}>
                <TextField
                  error={contactNameErr ? true : false}
                  label={contactNameErr || "Contact Name *"}
                  onChange={(e) => setContactName(e.target.value)}
                  value={contactName}
                  variant="outlined"
                />
              </FormControl>
            {/* </FormField> */}
          </FormRow>
          <FormRow>
            <FormField>
              <FormControl sx={{ bgcolor: "white", minWidth: 300 }}>
                <TextField
                  error={emailErr ? true : false}
                  label={emailErr || "Email *"}
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  variant="outlined"
                />
              </FormControl>
            </FormField>
            <FormField>
              <FormControl sx={{ bgcolor: "white", minWidth: 300 }}>
                <TextField
                  error={phoneErr ? true : false}
                  label={phoneErr || "Mobile Phone *"}
                  onChange={(e) => setPhone(e.target.value)}
                  value={phone}
                  variant="outlined"
                />
              </FormControl>
            </FormField>
          </FormRow>
          <ButtonGroup>
            <CancelButton onClick={toggleModal} type="button">
              Cancel
            </CancelButton>
            <SaveButton onClick={(e) => handleSaveContact(e)} type="submit">
              Save
            </SaveButton>
          </ButtonGroup>
        </Form>
      </Modal>
    </div>
  );
};

export default NewContactModal;
